import { scrubSentryEventSecrets } from '@common/config/analytics/sentryDataScrubbing'
import CONFIG, { APP_VERSION, isDev } from '@common/config/env'
import * as Sentry from '@sentry/react'
import { IS_FIREFOX } from '@web/constants/common'

/**
 * ZKX ships the same Sentry wiring to two products that report into ONE Sentry
 * project, so every event carries fields that tell them apart:
 *  - tag `app`: `extension` | `mobile-web`
 *  - tag `engine`: the extension build target (`webkit`, `gecko`, …) — `web` for mobile-web
 *  - tag `content`: which JS context sent it (`ui`, `background`, `worker`)
 *  - `release`: `extension-<engine>@<version>` | `mobile-web@<version>` — must match the
 *    release name the source maps are uploaded under (scripts/build-extensions.sh,
 *    scripts/build-mobile-web.sh), otherwise stack traces stay minified.
 *
 * Build-time `process.env.MOBILE_WEBAPP` (inlined by babel) is used instead of the
 * runtime `isMobileWebApp`, because the latter reads `window` and is therefore
 * always false inside the mobile-web main-controller Web Worker.
 */
export const SENTRY_APP: 'extension' | 'mobile-web' =
  process.env.MOBILE_WEBAPP === 'true' ? 'mobile-web' : 'extension'

const SENTRY_ENGINE = SENTRY_APP === 'mobile-web' ? 'web' : process.env.WEB_ENGINE || 'unknown'

export const SENTRY_RELEASE =
  SENTRY_APP === 'mobile-web'
    ? `mobile-web@${APP_VERSION}`
    : `extension-${process.env.WEB_ENGINE}@${APP_VERSION}`

export const getSentryTags = (content: 'ui' | 'background' | 'worker') => ({
  app: SENTRY_APP,
  engine: SENTRY_ENGINE,
  content
})

/**
 * Console and network breadcrumbs are OFF in every context (upstream Ambire keeps
 * them on). In ZKX they carry RAILGUN scan logs, RPC URLs (some with API keys in
 * the path) and request bodies — enough to link an EOA to a private balance even
 * after scrubbing. DOM/history breadcrumbs stay on in the UI; the background
 * config disables those too (no DOM in a service worker / Web Worker).
 */
export const SENTRY_BREADCRUMBS_OPTIONS = {
  console: false,
  fetch: false,
  xhr: false,
  sentry: true
}

export const CRASH_ANALYTICS_WEB_CONFIG: Sentry.BrowserOptions = {
  dsn: CONFIG.SENTRY_DSN_BROWSER_EXTENSION,
  environment: CONFIG.APP_ENV as string,
  release: SENTRY_RELEASE,
  // Disables sending personally identifiable information
  sendDefaultPii: false,
  // Passing only a few integrations without `defaultIntegrations: false` overrides
  // just those defaults — here the breadcrumbs one.
  integrations: [
    Sentry.breadcrumbsIntegration({ ...SENTRY_BREADCRUMBS_OPTIONS, dom: true, history: true })
  ],
  // No explicit `event` param type here: annotating this object as
  // Sentry.BrowserOptions lets `event`'s type be inferred contextually as the
  // narrower `ErrorEvent`, which scrubSentryEventSecrets (generic) then
  // preserves in its return type -- an explicit `event: Sentry.Event` param
  // widens both to the general Event union, which BrowserOptions rejects.
  beforeSend(event) {
    return scrubSentryEventSecrets(event)
  }
}

export const captureException = Sentry.captureException
export const captureMessage = Sentry.captureMessage
export const setExtraContext = Sentry.setExtra
export const setUserContext = Sentry.setUser
export const ErrorBoundary = Sentry.ErrorBoundary

/**
 * Since v5.15.0, we enable anonymous crash reporting by default,
 * because that's part of our privacy policy now.
 * Except in development. Even tho we tag errors with the environment,
 * we don't want to spam Sentry with errors that occur during development.
 * Since v5.21.2, disabled by default for Firefox. The flow must be different -
 * control mechanism must be shown at first-run of the add-on and should
 * contain a choice accompanied by the data collection summary.
 * ZKX: the Firefox opt-out is an add-on store policy, so it only applies to the
 * extension — mobile-web opened in Firefox follows the normal default.
 */
export const CRASH_ANALYTICS_ENABLED_DEFAULT =
  !isDev && (SENTRY_APP === 'mobile-web' || !IS_FIREFOX)

/**
 * Since v5.21.2, we store the crash analytics enabled state in a new storage key
 * to avoid conflicts with the previous storage key. Prev key: 'crashAnalyticsEnabled'.
 */
export const CRASH_ANALYTICS_ENABLED_STORAGE_KEY = 'crashAnalyticsEnabledV2'
